import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { Button, Container, Header, Icon, Message, Segment } from 'semantic-ui-react';

import { PATHS } from '../../paths';

/**
 * Confirming an email address, and what the person is offered immediately afterwards.
 *
 * **Public and unauthenticated**, like the password reset page: somebody following this link is
 * very often not signed in, and may be on a shared phone holding somebody else's session. The
 * token is the only credential involved and it identifies one person.
 *
 * The flow the owner asked for, in three states:
 *
 *   1. Thank you — confirmed, with a button offering a password reset.
 *   2. "Check your email again, a link is on the way" once they take it.
 *   3. A link that no longer works, said plainly and without explaining which way it failed.
 *
 * App store links are on every state, because somebody who has just proved they can read their
 * email is exactly the person who has not yet installed the app.
 */

const CONFIRM_EMAIL = gql`
  mutation ConfirmEmail($token: String!) {
    confirmEmail(token: $token) {
      success
      email
      mayRequestPasswordReset
    }
  }
`;

const REQUEST_RESET = gql`
  mutation RequestPasswordResetAfterConfirming($token: String!) {
    requestPasswordResetAfterConfirming(token: $token)
  }
`;

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.joyya.android';
const IOS_URL = 'https://apps.apple.com/us/app/joyya/id6761905788';

const GetTheApps = () => (
  <Segment basic style={{ paddingLeft: 0, paddingRight: 0 }}>
    <Header as="h4" style={{ marginBottom: '0.6em' }}>Get the Joyya app</Header>
    <Button as="a" href={IOS_URL} target="_blank" rel="noreferrer" style={{ marginBottom: '0.5em' }}>
      <Icon name="apple" /> iPhone or iPad
    </Button>
    <Button as="a" href={PLAY_URL} target="_blank" rel="noreferrer" style={{ marginBottom: '0.5em' }}>
      <Icon name="android" /> Android
    </Button>
  </Segment>
);

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [confirmEmail] = useMutation(CONFIRM_EMAIL);
  const [requestReset, { loading: requesting }] = useMutation(REQUEST_RESET);

  const [state, setState] = useState<'working' | 'confirmed' | 'reset-sent' | 'failed'>('working');
  const [email, setEmail] = useState<string | null>(null);
  const [mayReset, setMayReset] = useState(false);

  // Once. React 18 StrictMode double-invokes effects in development, and this mutation consumes a
  // single-use token — the second call would land on an already-used row. The server treats that
  // as success for exactly this reason, but firing it twice is still wrong.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (token === '') {
      setState('failed');
      return;
    }

    confirmEmail({ variables: { token } })
      .then(({ data }) => {
        if (data?.confirmEmail?.success) {
          setEmail(data.confirmEmail.email ?? null);
          setMayReset(data.confirmEmail.mayRequestPasswordReset === true);
          setState('confirmed');
        } else {
          setState('failed');
        }
      })
      .catch(() => setState('failed'));
  }, [confirmEmail, token]);

  const shell = (children: React.ReactNode) => (
    <Container style={{ paddingTop: '3em', paddingBottom: '3em', maxWidth: '560px' }}>
      {children}
    </Container>
  );

  if (state === 'working') {
    return shell(<p>Confirming your email address…</p>);
  }

  if (state === 'failed') {
    return shell(
      <>
        <Header as="h1">That link didn't work</Header>
        {/* Deliberately does not say whether it expired, was already used, or never existed —
            all three answer identically, so this cannot be used to probe for a live one. */}
        <p>
          It may have already been used, or it may have expired. If you still need to confirm your
          address, sign in and ask for a new link from Settings — or just use the app.
        </p>
        <Button as="a" href={PATHS.LOGIN} primary>Go to sign in</Button>
        <GetTheApps />
      </>,
    );
  }

  if (state === 'reset-sent') {
    return shell(
      <>
        <Header as="h1">Check your email again</Header>
        <Message positive>
          <Message.Header>A link is on the way</Message.Header>
          <p>
            We've sent a password reset link{email ? ` to ${email}` : ''}. Open it and choose a new
            password. It works once, and expires in 24 hours.
          </p>
        </Message>
        <p style={{ color: '#767676' }}>
          If it hasn't arrived in a few minutes, check your spam folder.
        </p>
        <GetTheApps />
      </>,
    );
  }

  return shell(
    <>
      <Header as="h1">Thank you</Header>
      <Message positive>
        <Message.Header>Your email address is confirmed</Message.Header>
        <p>
          {email ? <>We'll use <strong>{email}</strong> to reach you</> : <>We can reach you</>}
          {' '}— to reset your password if you forget it, and to tell you when anything important
          changes.
        </p>
      </Message>

      {mayReset && (
        <Segment>
          <Header as="h3" style={{ marginBottom: '0.2em' }}>
            Would you like to reset your password?
          </Header>
          <p style={{ color: '#767676' }}>
            Plenty of people confirm their address because they'd forgotten their password. If
            that's you, we can send a reset link now.
          </p>
          <Button
            primary
            loading={requesting}
            disabled={requesting}
            onClick={async () => {
              // Always moves on, even on failure. The mutation returns true regardless so it
              // cannot be used to probe, and leaving somebody on a spinner because a send was
              // refused would tell them nothing they can act on.
              try {
                await requestReset({ variables: { token } });
              } catch {
                /* ignored on purpose — see above */
              }
              setState('reset-sent');
            }}
          >
            Yes, send me a reset link
          </Button>
          <Button as="a" href={PATHS.LOGIN} style={{ marginLeft: '0.5em' }}>
            No thanks, I'll sign in
          </Button>
        </Segment>
      )}

      {!mayReset && (
        <Button as="a" href={PATHS.LOGIN} primary>Go to sign in</Button>
      )}

      <GetTheApps />
    </>
  );
};
